import React from 'react';
import './ShimmerLoader.css';

interface ShimmerLoaderProps {
  type: 'table' | 'form' | 'image' | 'team-row' | 'player-row' | 'tournament-card' | 'table-rows' | 'club-details' | 'dues-table' | 'due-detail';
  rows?: number;
  variant?: 'team' | 'player';
}

const ShimmerLoader: React.FC<ShimmerLoaderProps> = ({ type, rows = 5, variant = 'team' }) => {
  if (type === 'table') {
    return (
      <div className="shimmer-table">
        <div className="shimmer-header">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="shimmer-cell" />
          ))}
        </div>
        <div className="shimmer-body">
          {[...Array(rows)].map((_, rowIndex) => (
            <div key={rowIndex} className="shimmer-row">
              {[...Array(6)].map((_, colIndex) => (
                <div key={colIndex} className="shimmer-cell" />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'table-rows') {
    return (
      <div className="shimmer-table-rows">
        {[...Array(rows)].map((_, rowIndex) => (
          <div key={rowIndex} className={variant === 'team' ? 'shimmer-team-row' : 'shimmer-player-row'}>
            {variant === 'team' ? (
              <>
                <div className="shimmer-team-logo" />
                <div className="shimmer-team-info">
                  <div className="shimmer-team-name" />
                  <div className="shimmer-team-details">
                    <div className="shimmer-team-short-name" />
                    <div className="shimmer-team-age-categories" />
                  </div>
                </div>
                <div className="shimmer-team-stats">
                  <div className="shimmer-stat" />
                  <div className="shimmer-stat" />
                </div>
                <div className="shimmer-team-actions">
                  <div className="shimmer-action-btn" />
                  <div className="shimmer-action-btn" />
                  <div className="shimmer-action-btn" />
                </div>
              </>
            ) : (
              <>
                <div className="shimmer-player-profile" />
                <div className="shimmer-player-info">
                  <div className="shimmer-player-name" />
                  <div className="shimmer-player-details">
                    <div className="shimmer-player-dob" />
                    <div className="shimmer-player-age-category" />
                  </div>
                </div>
                <div className="shimmer-player-status">
                  <div className="shimmer-status-badge" />
                  <div className="shimmer-status-badge" />
                </div>
                <div className="shimmer-player-actions">
                  <div className="shimmer-action-btn" />
                  <div className="shimmer-action-btn" />
                  <div className="shimmer-action-btn" />
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (type === 'form') {
    return (
      <div className="shimmer-form">
        <div className="shimmer-form-group">
          <div className="shimmer-label" />
          <div className="shimmer-input" />
        </div>
        <div className="shimmer-form-group">
          <div className="shimmer-label" />
          <div className="shimmer-input" />
        </div>
        <div className="shimmer-form-group">
          <div className="shimmer-label" />
          <div className="shimmer-input" style={{ height: '200px' }} />
        </div>
      </div>
    );
  }

  if (type === 'image') {
    return (
      <div className="shimmer-image">
        <div className="shimmer-image-placeholder" />
      </div>
    );
  }

  if (type === 'team-row') {
    return (
      <div className="shimmer-team-row">
        <div className="shimmer-team-logo" />
        <div className="shimmer-team-info">
          <div className="shimmer-team-name" />
          <div className="shimmer-team-details">
            <div className="shimmer-team-short-name" />
            <div className="shimmer-team-age-categories" />
          </div>
        </div>
        <div className="shimmer-team-stats">
          <div className="shimmer-stat" />
          <div className="shimmer-stat" />
        </div>
        <div className="shimmer-team-actions">
          <div className="shimmer-action-btn" />
          <div className="shimmer-action-btn" />
          <div className="shimmer-action-btn" />
        </div>
      </div>
    );
  }

  if (type === 'player-row') {
    return (
      <div className="shimmer-player-row">
        <div className="shimmer-player-profile" />
        <div className="shimmer-player-info">
          <div className="shimmer-player-name" />
          <div className="shimmer-player-details">
            <div className="shimmer-player-dob" />
            <div className="shimmer-player-age-category" />
          </div>
        </div>
        <div className="shimmer-player-status">
          <div className="shimmer-status-badge" />
          <div className="shimmer-status-badge" />
        </div>
        <div className="shimmer-player-actions">
          <div className="shimmer-action-btn" />
          <div className="shimmer-action-btn" />
          <div className="shimmer-action-btn" />
        </div>
      </div>
    );
  }

  if (type === 'tournament-card') {
    return (
      <div className="shimmer-tournament-card">
        <div className="shimmer-tournament-header">
          <div className="shimmer-tournament-logo" />
          <div className="shimmer-tournament-info">
            <div className="shimmer-tournament-name" />
            <div className="shimmer-tournament-location" />
          </div>
        </div>
        <div className="shimmer-tournament-details">
          <div className="shimmer-detail-row">
            <div className="shimmer-detail-label" />
            <div className="shimmer-detail-value" />
          </div>
          <div className="shimmer-detail-row">
            <div className="shimmer-detail-label" />
            <div className="shimmer-detail-value" />
          </div>
        </div>
        <div className="shimmer-tournament-actions">
          <div className="shimmer-action-btn" />
          <div className="shimmer-action-btn" />
        </div>
      </div>
    );
  }

  if (type === 'club-details') {
    return (
      <div className="shimmer-club-details">
        <div className="shimmer-club-header">
          <div className="shimmer-club-logo" />
          <div className="shimmer-club-info">
            <div className="shimmer-club-name" />
            <div className="shimmer-club-subtitle" />
          </div>
        </div>
        <div className="shimmer-club-form">
          <div className="shimmer-form-row">
            <div className="shimmer-form-group">
              <div className="shimmer-label" />
              <div className="shimmer-input" />
            </div>
            <div className="shimmer-form-group">
              <div className="shimmer-label" />
              <div className="shimmer-input" />
            </div>
          </div>
          <div className="shimmer-form-row">
            <div className="shimmer-form-group">
              <div className="shimmer-label" />
              <div className="shimmer-input" />
            </div>
            <div className="shimmer-form-group">
              <div className="shimmer-label" />
              <div className="shimmer-input" />
            </div>
          </div>
          <div className="shimmer-form-row">
            <div className="shimmer-form-group">
              <div className="shimmer-label" />
              <div className="shimmer-input" />
            </div>
          </div>
          <div className="shimmer-form-actions">
            <div className="shimmer-action-btn" />
          </div>
        </div>
      </div>
    );
  }

  if (type === 'dues-table') {
    return (
      <div className="shimmer-dues-table">
        <div className="shimmer-dues-header">
          <div className="shimmer-dues-cell">Tournament Name</div>
          <div className="shimmer-dues-cell">Teams</div>
          <div className="shimmer-dues-cell">Age Category</div>
          <div className="shimmer-dues-cell">Total Amount</div>
          <div className="shimmer-dues-cell">Amount Paid</div>
          <div className="shimmer-dues-cell">Total Balance Due</div>
          <div className="shimmer-dues-cell">Due Date</div>
          <div className="shimmer-dues-cell">CTAs</div>
        </div>
        <div className="shimmer-dues-body">
          {[...Array(rows)].map((_, rowIndex) => (
            <div key={rowIndex} className="shimmer-dues-row">
              <div className="shimmer-dues-cell">
                <div className="shimmer-dues-text" style={{ width: '150px' }} />
              </div>
              <div className="shimmer-dues-cell">
                <div className="shimmer-dues-text" style={{ width: '60px' }} />
              </div>
              <div className="shimmer-dues-cell">
                <div className="shimmer-dues-text" style={{ width: '40px' }} />
              </div>
              <div className="shimmer-dues-cell">
                <div className="shimmer-dues-text" style={{ width: '80px' }} />
              </div>
              <div className="shimmer-dues-cell">
                <div className="shimmer-dues-text" style={{ width: '80px' }} />
              </div>
              <div className="shimmer-dues-cell">
                <div className="shimmer-dues-text" style={{ width: '80px' }} />
              </div>
              <div className="shimmer-dues-cell">
                <div className="shimmer-dues-text" style={{ width: '90px' }} />
              </div>
              <div className="shimmer-dues-cell">
                <div className="shimmer-dues-button" style={{ width: '80px' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'due-detail') {
    return (
      <div className="shimmer-due-detail">
        <div className="shimmer-due-detail-header">
          <div className="shimmer-due-detail-title" />
          <div className="shimmer-due-detail-subtitle" />
        </div>
        <div className="shimmer-due-detail-content">
          <div className="shimmer-due-detail-card">
            <div className="shimmer-due-detail-card-header">
              <div className="shimmer-due-detail-card-title" />
              <div className="shimmer-due-detail-card-subtitle" />
            </div>
            <div className="shimmer-due-detail-bill-breakdown">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="shimmer-due-detail-bill-row">
                  <div className="shimmer-due-detail-bill-label" />
                  <div className="shimmer-due-detail-bill-value" />
                </div>
              ))}
            </div>
            <div className="shimmer-due-detail-total">
              <div className="shimmer-due-detail-total-label" />
              <div className="shimmer-due-detail-total-value" />
            </div>
            <div className="shimmer-due-detail-actions">
              <div className="shimmer-due-detail-button" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ShimmerLoader; 